import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import {
  Copy, ExternalLink, Check, Lock, Link as LinkIcon, Loader2,
  Code, QrCode as QrCodeIcon,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import SocialShareButtons from '../shared/SocialShareButtons';
import { useToast } from '../../hooks/use-toast';
import PeopleWithAccessSection from './PeopleWithAccessSection';

/**
 * ShareDesignModal — three modes:
 *
 *   "design"   — current user owns a design. Sharing creates a public
 *                view-only link to that specific design. Features:
 *                Access Level (Private / Anyone with Link), People
 *                With Access, Social row + QR code.
 *
 *   "template" — admin or template-owning designer is editing/viewing
 *                a template. Features: Access Level (Private / Link /
 *                Public), People With Access, Social row, Use Template,
 *                Stats, Embed (at bottom).
 *
 *   "publish"  — current doc is neither owned by the user nor a
 *                template. Prompts the user to publish first.
 *
 * Architecture decisions enforced by recent feedback:
 *   - No thumbnail header. The modal is for sharing, not browsing.
 *   - Access Level is a real radio, not a row of independent toggles.
 *   - People With Access has its own section (Owner / Editors /
 *     Viewers / Invite People). Collaborator lists are server-stubbed
 *     for now; the section renders the Owner from props and reserves
 *     space for the Editors/Viewers UI as the backend lands.
 *   - Embed is at the very bottom of Template mode — advanced action,
 *     not a primary affordance.
 *   - Design mode works for ANY owned design, not only templates.
 */
export default function ShareDesignModal({
  open,
  onClose,
  // Template-mode props
  templateId = null,
  templateName = null,
  shareSettings = null,
  templateStats = null,            // { views, uses, png_downloads, jpg_downloads, copies_created }
  canEditSettings = false,
  onSaveShareSettings = null,      // async (next_share_settings) => void
  onOpenPreview = null,
  onPublishAsTemplate = null,
  // Design-mode props
  designId = null,
  designName = null,
  designShareToken = null,
  onEnableDesignShare = null,      // async () => token
  onRevokeDesignShare = null,      // async () => void
  // Common — who renders in the "Owner" slot of People With Access.
  ownerDisplayName = null,
  ownerIsCurrentUser = false,
  // C9 — current user id so PeopleWithAccessSection can show "(You)" /
  // "Leave" menus and the kebab actions can target the right principal.
  // null is fine; just disables self-only menu items.
  currentUserId = null,
}) {
  const { toast } = useToast();

  // Mode resolution:
  //   templateId set                       → template
  //   designId set + share controllers     → design
  //   neither                              → publish (rare)
  const mode = templateId
    ? 'template'
    : (designId && (onEnableDesignShare || designShareToken) ? 'design' : 'publish');

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const templateUrl = templateId ? `${origin}/template/${templateId}` : null;
  const designUrl = designShareToken ? `${origin}/design/${designShareToken}` : null;
  const shareUrl = templateUrl || designUrl;

  // ────────── Access Level (radio) ──────────
  // Template mode: Private / Anyone with Link / Public.
  // Design mode:   Private / Anyone with Link.
  //
  // For templates this maps to share_settings.is_public (Private=false,
  // Link or Public both → true). 'public' adds discoverability when the
  // backend later supports a separate listing flag; for now Link and
  // Public are equivalent server-side and the radio surfaces intent.
  // Two-state today (private | link). The backend may still carry
  // access_level="public" on legacy rows promoted before C12-fix; we
  // collapse those to "link" for display so the two-radio UI stays
  // consistent. When the marketplace ships, restore the public radio.
  const templateAccessLevel = (() => {
    const raw = shareSettings?.access_level;
    if (raw === 'private') return 'private';
    if (raw === 'link' || raw === 'public') return 'link';
    if (!shareSettings) return 'link';
    return shareSettings.is_public === false ? 'private' : 'link';
  })();
  const designAccessLevel = designShareToken ? 'link' : 'private';

  const [draftLevel, setDraftLevel] = useState(
    mode === 'template' ? templateAccessLevel : designAccessLevel
  );
  useEffect(() => {
    setDraftLevel(mode === 'template' ? templateAccessLevel : designAccessLevel);
  }, [mode, templateAccessLevel, designAccessLevel, open]);

  const [savingLevel, setSavingLevel] = useState(false);

  // ────────── Clipboard helpers ──────────
  const writeClipboard = useCallback(async (text) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      return true;
    } catch {
      return false;
    }
  }, []);

  const [copied, setCopied] = useState(false);
  const handleCopyLink = useCallback(async () => {
    if (!shareUrl) return;
    if (await writeClipboard(shareUrl)) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: 'Link copied' });
    }
  }, [shareUrl, writeClipboard, toast]);

  // ────────── Embed snippet (Template only) ──────────
  const embedCode = templateUrl
    ? `<iframe src="${templateUrl}" width="600" height="750" frameborder="0" allowfullscreen></iframe>`
    : '';
  const [embedCopied, setEmbedCopied] = useState(false);
  const handleCopyEmbed = useCallback(async () => {
    if (!embedCode) return;
    if (await writeClipboard(embedCode)) {
      setEmbedCopied(true);
      setTimeout(() => setEmbedCopied(false), 2000);
      toast({ title: 'Embed code copied' });
    }
  }, [embedCode, writeClipboard, toast]);

  // ────────── Design share enable/revoke ──────────
  const [designShareLoading, setDesignShareLoading] = useState(false);
  const [designShareError, setDesignShareError] = useState(null);
  const handleEnable = useCallback(async () => {
    if (!onEnableDesignShare) return;
    setDesignShareLoading(true);
    setDesignShareError(null);
    try { await onEnableDesignShare(); }
    catch (e) { setDesignShareError(e?.message || 'Could not enable sharing.'); }
    finally { setDesignShareLoading(false); }
  }, [onEnableDesignShare]);
  const handleRevoke = useCallback(async () => {
    if (!onRevokeDesignShare) return;
    setDesignShareLoading(true);
    setDesignShareError(null);
    try { await onRevokeDesignShare(); }
    catch (e) { setDesignShareError(e?.message || 'Could not revoke.'); }
    finally { setDesignShareLoading(false); }
  }, [onRevokeDesignShare]);

  // ────────── Access Level save (Template mode only) ──────────
  // Design mode maps level changes directly to enable/revoke calls.
  // Template mode persists via onSaveShareSettings({is_public: bool}).
  const handleAccessLevelChange = useCallback(async (next) => {
    setDraftLevel(next);
    if (mode === 'design') {
      if (next === 'private' && designShareToken) await handleRevoke();
      else if (next === 'link' && !designShareToken) await handleEnable();
      return;
    }
    if (mode === 'template' && onSaveShareSettings && shareSettings) {
      // Send access_level (private | link) explicitly. Parent forwards
      // it to the admin/designer update endpoint; backend dual-writes
      // the legacy is_public boolean.
      if (next === templateAccessLevel) return; // no-op
      const previousLevel = templateAccessLevel;
      setSavingLevel(true);
      setDesignShareError(null);
      try {
        await onSaveShareSettings({
          ...shareSettings,
          is_public: next !== 'private',
          access_level: next,
        });
      } catch (err) {
        // Explicit revert: without this, the useEffect below
        // eventually snaps the radio back when shareSettings is
        // recomputed on the next render, but the in-between optimistic
        // state confuses the user. Reset NOW + surface the failure.
        setDraftLevel(previousLevel);
        setDesignShareError(
          err?.message
            || "We couldn't update access level. Please try again."
        );
      } finally {
        setSavingLevel(false);
      }
    }
  }, [mode, designShareToken, handleRevoke, handleEnable, onSaveShareSettings, shareSettings, templateAccessLevel]);

  const headerTitle =
    mode === 'template' ? 'Share Template' :
    mode === 'design'   ? 'Share Design'   :
    'Share Design';

  const showLinkSection = (
    (mode === 'template' && draftLevel !== 'private') ||
    (mode === 'design' && Boolean(designUrl))
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose && onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle>{headerTitle}</DialogTitle>
          {mode === 'template' && templateName && (
            <p className="text-sm text-gray-500 mt-0.5 truncate">{templateName}</p>
          )}
          {mode === 'design' && designName && (
            <p className="text-sm text-gray-500 mt-0.5 truncate">{designName}</p>
          )}
        </DialogHeader>

        {mode === 'publish' && (
          <div className="py-3 space-y-3">
            <p className="text-sm text-gray-700">
              This document can't be shared yet. Publish it as a template or
              save it to your account first.
            </p>
          </div>
        )}

        {(mode === 'template' || mode === 'design') && (
          <div className="space-y-5 py-2">

            {/* ─── 1. Access Level ─────────────────────────────────────── */}
            <section>
              <p className="text-xs font-semibold text-gray-600 mb-2">
                Access Level
                {savingLevel && (
                  <Loader2 className="inline w-3 h-3 ml-2 animate-spin text-gray-400" />
                )}
              </p>
              <div className="space-y-1.5" role="radiogroup" aria-label="Access Level">
                <AccessLevelRow
                  Icon={Lock}
                  label="Private"
                  description="Only you and people you invite can open this."
                  value="private"
                  selected={draftLevel === 'private'}
                  disabled={!canEditAccess(mode, canEditSettings, onEnableDesignShare, onRevokeDesignShare) || savingLevel}
                  onSelect={handleAccessLevelChange}
                />
                <AccessLevelRow
                  Icon={LinkIcon}
                  label="Anyone with Link"
                  description="Anyone who has the link can view."
                  value="link"
                  selected={draftLevel === 'link'}
                  disabled={!canEditAccess(mode, canEditSettings, onEnableDesignShare, onRevokeDesignShare) || savingLevel}
                  onSelect={handleAccessLevelChange}
                />
                {/* Public access level intentionally hidden: until the
                    public marketplace / discovery feed ships, Public and
                    Anyone-with-Link produce identical user-visible
                    behavior. Re-introduce when the gallery is live. */}
              </div>
              {designShareError && (
                <p className="text-[11px] text-red-600 mt-2">{designShareError}</p>
              )}
            </section>

            {/* ─── 2. Public Link (only when not Private) ──────────────── */}
            {showLinkSection && shareUrl && (
              <section>
                <label htmlFor="share-url" className="block text-xs font-semibold text-gray-600 mb-1.5">
                  Public link
                </label>
                <div className="flex gap-2">
                  <input
                    id="share-url"
                    type="text"
                    readOnly
                    value={shareUrl}
                    onFocus={(e) => e.target.select()}
                    className="flex-1 min-w-0 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-md font-mono"
                    data-testid="share-url"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleCopyLink}
                    className="flex-shrink-0"
                    data-testid="share-copy-btn"
                  >
                    {copied ? (
                      <><Check className="w-4 h-4 mr-1" /> Copied</>
                    ) : (
                      <><Copy className="w-4 h-4 mr-1" /> Copy Link</>
                    )}
                  </Button>
                </div>
              </section>
            )}

            {/* ─── 3. People With Access (C9 — real collaborator list) ──── */}
            <PeopleWithAccessSection
              resourceKind={mode === 'template' ? 'template' : 'design'}
              resourceId={mode === 'template' ? templateId : designId}
              resourceName={mode === 'template' ? templateName : designName}
              currentUserId={currentUserId}
              isOwner={ownerIsCurrentUser}
              ownerDisplayName={ownerDisplayName}
            />
            {/* Owner slot below is intentionally kept; it's now rendered
                inside PeopleWithAccessSection so the placeholder UI
                doesn't double up. PersonRow remains exported for the
                "publish" mode below. */}

            {/* ─── 4. Share To (social + QR for design) ────────────────── */}
            {shareUrl && (
              <section>
                <p className="text-xs font-semibold text-gray-600 mb-2">Share to</p>
                <SocialShareButtons
                  url={shareUrl}
                  title={mode === 'template'
                    ? (templateName ? `Check out "${templateName}" on iVisora` : 'Check out this template on iVisora')
                    : (designName  ? `Take a look at "${designName}" on iVisora`  : 'Take a look at this design on iVisora')}
                  description={mode === 'template'
                    ? (templateName ? `${templateName} — open in iVisora` : 'Open in iVisora')
                    : (designName  ? `${designName} — view on iVisora`   : 'View this design on iVisora')}
                  onCopy={() => toast({ title: 'Link copied' })}
                />
                {/* QR Code is Design Sharing specific per spec. */}
                {mode === 'design' && (
                  <div className="mt-3 flex items-center gap-3" data-testid="design-share-qr">
                    <div className="bg-white p-2 rounded-md border border-gray-200">
                      <QRCodeSVG value={shareUrl} size={88} level="M" />
                    </div>
                    <div className="text-xs text-gray-500">
                      <p className="flex items-center gap-1 font-medium text-gray-700">
                        <QrCodeIcon className="w-3.5 h-3.5" /> QR Code
                      </p>
                      <p className="mt-0.5">Scan to open this design on a mobile device.</p>
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* ─── 5. Template-only: Use Template + Stats ──────────────── */}
            {mode === 'template' && (
              <section className="space-y-3">
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Use Template</p>
                  <p className="text-xs text-gray-500">
                    Recipients who open the public link can either preview the
                    template or use it in their own editor (Pro users get an
                    editable copy; free users get a temporary shared session).
                  </p>
                </div>
                {templateStats && (
                  <div className="grid grid-cols-3 gap-2 text-center" data-testid="template-stats">
                    <StatTile label="Analytics" value={templateStats.views || 0} sub="Views" />
                    <StatTile
                      label="Downloads"
                      value={((templateStats.png_downloads || 0) + (templateStats.jpg_downloads || 0))}
                      sub="PNG + JPG"
                    />
                    <StatTile label="Copies" value={templateStats.copies_created || 0} sub="Created" />
                  </div>
                )}
              </section>
            )}

            {/* ─── 6. Embed (Template only, bottom — advanced) ─────────── */}
            {mode === 'template' && templateUrl && (
              <section data-testid="embed-section">
                <details className="border border-gray-100 rounded-md">
                  <summary className="cursor-pointer text-xs font-semibold text-gray-600 px-3 py-2 hover:bg-gray-50 flex items-center gap-1.5">
                    <Code className="w-3.5 h-3.5" /> Embed code
                  </summary>
                  <div className="p-3 border-t border-gray-100">
                    <p className="text-[11px] text-gray-500 mb-2">
                      Paste this snippet into a webpage to embed the template preview.
                    </p>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={embedCode}
                        onFocus={(e) => e.target.select()}
                        className="flex-1 min-w-0 px-2 py-1.5 text-xs bg-gray-50 border border-gray-200 rounded font-mono"
                        data-testid="share-template-embed"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleCopyEmbed}
                        data-testid="share-embed-copy-btn"
                        className="flex-shrink-0"
                      >
                        {embedCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </details>
              </section>
            )}
          </div>
        )}

        <DialogFooter>
          {mode === 'template' && (
            <>
              <Button variant="outline" onClick={onClose}>Close</Button>
              {onOpenPreview && (
                <Button onClick={() => onOpenPreview()} data-testid="share-open-preview-btn">
                  <ExternalLink className="w-4 h-4 mr-1" /> Open Preview
                </Button>
              )}
            </>
          )}
          {mode === 'design' && (
            <>
              <Button variant="outline" onClick={onClose}>Close</Button>
              {designShareToken && (
                <Button
                  variant="outline"
                  onClick={handleRevoke}
                  disabled={designShareLoading}
                  data-testid="share-design-revoke-btn"
                >
                  {designShareLoading ? (
                    <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Revoking</>
                  ) : (
                    <>Revoke link</>
                  )}
                </Button>
              )}
            </>
          )}
          {mode === 'publish' && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              {onPublishAsTemplate && (
                <Button onClick={() => onPublishAsTemplate()} data-testid="share-publish-btn">
                  Publish as Template
                </Button>
              )}
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function canEditAccess(mode, canEditSettings, onEnable, onRevoke) {
  if (mode === 'template') return canEditSettings;
  if (mode === 'design')   return Boolean(onEnable && onRevoke);
  return false;
}

function AccessLevelRow({ Icon, label, description, value, selected, disabled, onSelect }) {
  return (
    <label
      className={`flex items-start gap-3 rounded-md border p-2.5 cursor-pointer transition-colors ${
        selected ? 'border-violet-300 bg-violet-50' : 'border-gray-100 hover:bg-gray-50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      data-testid={`access-level-${value}`}
    >
      <input
        type="radio"
        name="access-level"
        value={value}
        checked={selected}
        disabled={disabled}
        onChange={() => onSelect(value)}
        className="mt-1"
      />
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${selected ? 'text-violet-600' : 'text-gray-500'}`} />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800">{label}</p>
        <p className="text-[11px] text-gray-500">{description}</p>
      </div>
    </label>
  );
}

function StatTile({ label, value, sub }) {
  return (
    <div className="border border-gray-100 rounded-md py-2">
      <p className="text-[10px] uppercase tracking-wide text-gray-400">{label}</p>
      <p className="text-base font-semibold text-gray-900">{value.toLocaleString()}</p>
      <p className="text-[10px] text-gray-500">{sub}</p>
    </div>
  );
}
